import { boundedItems } from "@/shared/foundation/primitives/ConfirmationCard/boundedItems";

import styles from "./SignalList.module.css";
import { groupSignalsByTopic, useResolvedSignals } from "./useResolvedSignals";

/**
 * How many signals to render before the rest collapse to a count. A batch is
 * capped at 20 by `execute`, so this only bites on a proposal that is already
 * being refused.
 */
const SIGNAL_LIMIT = 25;

/**
 * The signals a visualizer tool proposes, named by topic and field.
 *
 * Grouping is the point: the model is told to put related signals on one
 * panel, so the usual proposal is several fields of one topic. Stating the
 * topic once and listing the fields under it is the difference between
 * something a reader checks and something they skip.
 */
export function SignalList({
  messagePathIds,
}: {
  messagePathIds: readonly string[];
}) {
  const signals = useResolvedSignals(messagePathIds);
  const { shown, hiddenCount } = boundedItems(messagePathIds, SIGNAL_LIMIT);
  const shownIds = new Set(shown);
  const groups = groupSignalsByTopic(
    signals.filter((signal) => shownIds.has(signal.messagePathId)),
  );

  return (
    <div className={styles.groups}>
      {groups.map((group, index) => (
        <div key={group.topicName ?? `pending-${index}`}>
          {group.topicName !== undefined && (
            <div className={styles.topicName}>{group.topicName}</div>
          )}
          <ul className={styles.fields}>
            {group.signals.map((signal) => (
              <li
                key={signal.messagePathId}
                className={
                  signal.fieldPath === undefined
                    ? styles.pendingId
                    : styles.field
                }
              >
                {signal.fieldPath ?? signal.messagePathId}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {hiddenCount > 0 && (
        <div className={styles.pendingId}>and {hiddenCount} more</div>
      )}
    </div>
  );
}
